import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { peopleGateway } from '../../index';
import { employmentStatusLabels, type EmployeeResponse } from '../../types';
import { EmployeeAddressSection } from './EmployeeAddressSection';
import { EmployeePayMethodSection } from './EmployeePayMethodSection';
import { EmployeePayScheduleSection } from './EmployeePayScheduleSection';
import { EmployeeRoleSection } from './EmployeeRoleSection';
import { PayrollDetailsSection } from './PayrollDetailsSection';
import { WorkDetailsSection, WorkLocationSection } from './EmployeeWorkSections';

export interface EmployeeProfileStepProps {
  employee: EmployeeResponse;
  /** Only EmployeeSetupStep's Personal Information subsection calls this today — after a real save via
   * peopleGateway.updateEmployee, it hands the fresh EmployeeResponse back up so the profile header (name)
   * reflects it immediately without a full reload. Other steps simply don't use it. */
  onEmployeeUpdated?: (updated: EmployeeResponse) => void;
  /** Called after a pay rate was saved or ended, so the profile re-reads the server's readiness result. */
  onPayRatesChanged?: () => void;
  /** Called with the First and Last Name inputs' current text on every keystroke, so the profile heading can preview it before Save. */
  onNameTyped?: (first: string, last: string) => void;
  /** The employment status pill drawn above the section list (Figma). Display only. */
  statusPill?: ReactNode;
  /** The footer action bar. Steps with a section list draw it INSIDE their bordered panel, like Figma; the profile draws it itself for the others. */
  footer?: ReactNode;
}

/** Only the last four digits of an SSN are ever stored (docs/ssn-security-design.md: full-SSN storage is not approved). */
const SSN_LAST4_REFUSAL = 'Enter only the last four digits. A full Social Security number is not stored.';

/**
 * Applies a keystroke or paste to the SSN last-4 box. A value that is not 0 to 4 digits is REFUSED, never trimmed: trimming a pasted
 * "123456789" down to its first four digits would silently save the wrong last four, and refusing keeps a full SSN out of component state.
 */
function applySsnLast4Input(raw: string): { value: string | null; refusal: string | null } {
  return /^\d{0,4}$/.test(raw) ? { value: raw, refusal: null } : { value: null, refusal: SSN_LAST4_REFUSAL };
}

const SUBSECTIONS = [
  { key: 'personal', label: 'Personal Information' },
  { key: 'address', label: 'Home/Residence Address' },
  { key: 'work', label: 'Work Details' },
  { key: 'location', label: 'Work Location' },
  { key: 'payment', label: 'Pay Method and Bank Details' },
  { key: 'payinfo', label: 'Pay Information' },
  { key: 'payroll', label: 'Payroll Details' },
] as const;

type SubsectionKey = (typeof SUBSECTIONS)[number]['key'];

function subsectionFromParam(value: string | null): SubsectionKey {
  return SUBSECTIONS.find((s) => s.key === value)?.key ?? 'personal';
}

// Field-by-field status (EPN-150, slice 1 of EPN-149):
// REAL (loaded + editable + persisted via peopleGateway.updateEmployee): Employee #, First Name, Last Name, Email,
//   Phone (with country code), Gender, Date of Birth, Hiring Date, New Hire.
// REAL (persisted via peopleGateway.changeEmployeeStatus, the server's separate narrow endpoint): Status and
//   Termination Date.
// REAL (persisted via peopleGateway.updateEmployee): the LAST FOUR digits of the SSN only. A full SSN is refused in the browser and by the
//   server until the approved encryption design exists (EPN-119, docs/ssn-security-design.md).
// Termination Date is always editable; entering a date sets Status to Terminated (nothing is saved until Save).
// REAL, read-only (Job Role Library; see EmployeeRoleSection.tsx): the current job role and its assignment history.
// REAL (EPN-152, slice 3a; see EmployeeWorkSections.tsx): Work Details department/job title, and Work Location
//   (primary location with effective date and history, allowed secondary locations, work arrangement).
// REAL (EPN-153, slice 2; see EmployeeAddressSection.tsx): Home/Residence Address (with history) and the optional Mailing Address.
// REAL (EPN-188; see EmployeePayMethodSection.tsx): Pay Method (Check only for now). Bank details stay unavailable.
// REAL (EPN-185; see EmployeePayRatesPanel.tsx): Payroll Details pay rates (list, add with optional rate template,
//   end) via compensationGateway / payConfigGateway; saving re-reads readiness through onPayRatesChanged.
// REAL (EPN-192; see EmployeePayScheduleSection.tsx): Pay Information, the employee's effective-dated assignment to one
//   company pay schedule via peopleGateway; pay day and period start are read-only from PayrollConfig's schedule.
// Only the bank fields under Pay Method stay disabled (no secure bank storage yet); no fake save, no localStorage.
export function EmployeeSetupStep({ employee, onEmployeeUpdated, onPayRatesChanged, onNameTyped, statusPill, footer }: EmployeeProfileStepProps) {
  // EPN-94: `?section=<key>` deep-links straight to a sub-section (the Setup Summary's links use it). An
  // unknown/missing value falls back to Personal Information. Derived during render when the param changes
  // (React's "adjusting state when a prop changes" pattern), like the employee buffer below.
  const [searchParams] = useSearchParams();
  const requestedSection = searchParams.get('section');
  const [sub, setSub] = useState<SubsectionKey>(() => subsectionFromParam(requestedSection));
  const [priorRequestedSection, setPriorRequestedSection] = useState(requestedSection);
  if (priorRequestedSection !== requestedSection) {
    setPriorRequestedSection(requestedSection);
    setSub(subsectionFromParam(requestedSection));
  }
  const [firstName, setFirstName] = useState(employee.firstName);
  const [lastName, setLastName] = useState(employee.lastName);
  const [email, setEmail] = useState(employee.email);
  const [employeeNumber, setEmployeeNumber] = useState(employee.employeeNumber);
  const [phone, setPhone] = useState(employee.phone ?? '');
  const [gender, setGender] = useState(employee.gender ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(employee.dateOfBirth ?? '');
  const [hireDate, setHireDate] = useState(employee.hireDate);
  const [isNewHire, setIsNewHire] = useState(employee.isNewHire ?? false);
  const [status, setStatus] = useState(employee.status);
  const [terminationDate, setTerminationDate] = useState(employee.terminationDate ?? '');
  const [ssnLast4, setSsnLast4] = useState(employee.ssnLast4 ?? '');
  const [ssnNotice, setSsnNotice] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [priorEmployee, setPriorEmployee] = useState(employee);

  // Re-sync the editable buffer whenever a (possibly refreshed) employee arrives — e.g. after this same
  // save completes, or if the route's employeeId changes. Derived during render (not an effect) per
  // React's "adjusting state when a prop changes" pattern — avoids an extra commit/re-render pass.
  if (priorEmployee !== employee) {
    setPriorEmployee(employee);
    setFirstName(employee.firstName);
    setLastName(employee.lastName);
    setEmail(employee.email);
    setEmployeeNumber(employee.employeeNumber);
    setPhone(employee.phone ?? '');
    setGender(employee.gender ?? '');
    setDateOfBirth(employee.dateOfBirth ?? '');
    setHireDate(employee.hireDate);
    setIsNewHire(employee.isNewHire ?? false);
    setStatus(employee.status);
    setTerminationDate(employee.terminationDate ?? '');
    setSsnLast4(employee.ssnLast4 ?? '');
    setSsnNotice(null);
    setSaveError(null);
  }

  async function handleSavePersonalInfo() {
    setSaveError(null);
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !employeeNumber.trim() || !hireDate) {
      setSaveError('Employee #, First Name, Last Name, Email, and Hiring Date are all required.');
      return;
    }
    if (status === 2 && !terminationDate) {
      setSaveError('Enter the Termination Date for a terminated employee.');
      return;
    }
    if (ssnLast4 !== '' && ssnLast4.length !== 4) {
      setSaveError('SSN must be exactly the last four digits, or left empty.');
      return;
    }
    setSaving(true);
    let updated = employee;
    try {
      // Validation is the server's job (phone country code, duplicate Employee #, date order): its message is shown as is.
      updated = await peopleGateway.updateEmployee(employee.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        dateOfBirth: dateOfBirth || null,
        ssnLast4: ssnLast4 || null,
        departmentId: employee.departmentId,
        jobTitleId: employee.jobTitleId,
        hireDate,
        employeeNumber: employeeNumber.trim(),
        phone: phone.trim() || null,
        gender: gender.trim() || null,
        isNewHire,
        employmentType: employee.employmentType ?? null,
      });
      onEmployeeUpdated?.(updated);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save Personal Information.');
      setSaving(false);
      return;
    }
    const wantedTermination = status === 2 ? terminationDate : null;
    if (status !== updated.status || wantedTermination !== (updated.terminationDate ?? null)) {
      try {
        updated = await peopleGateway.changeEmployeeStatus(employee.id, status, wantedTermination);
        onEmployeeUpdated?.(updated);
      } catch (err) {
        setSaveError(
          `Personal Information was saved, but the status change was not: ${
            err instanceof Error ? err.message : 'Could not save the employment status.'
          }`,
        );
      }
    }
    setSaving(false);
  }

  return (
    <div className="employee-setup-step profile-step-body">
      <div className="profile-side">
        {statusPill}
        <nav className="profile-subnav" aria-label="Employee Setup subsections">
          {SUBSECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={s.key === sub ? 'profile-subnav-item--active' : ''}
              onClick={() => setSub(s.key)}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="profile-step-content">
        {sub === 'personal' && (
          <section className="profile-section">
            <h3 className="profile-section-title">Personal Information</h3>
            <div className="profile-field-grid">
              <label>
                Employee #
                <input value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)} required />
              </label>
              <label>
                First Name
                <input value={firstName} onChange={(e) => { setFirstName(e.target.value); onNameTyped?.(e.target.value, lastName); }} required />
              </label>
              <label>
                Last Name
                <input value={lastName} onChange={(e) => { setLastName(e.target.value); onNameTyped?.(firstName, e.target.value); }} required />
              </label>
              <label>
                Email Address
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </label>
              <label>
                Phone Number
                <input
                  type="tel"
                  value={phone}
                  placeholder="+1 555 555 0123"
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
              <label>
                Gender
                <input value={gender} maxLength={50} onChange={(e) => setGender(e.target.value)} />
              </label>
              <label>
                Date of Birth
                <input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
              </label>
              <label>
                {isNewHire ? 'Hiring Date' : 'Original Hiring Date'}
                <input type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)} required />
              </label>
              <label>
                <input type="checkbox" checked={isNewHire} onChange={(e) => setIsNewHire(e.target.checked)} /> New hire
              </label>
              <label>
                Status
                <select
                  value={status}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setStatus(next);
                    // A termination date only belongs to a terminated employee, so moving away from Terminated clears it (it is never saved).
                    if (next !== 2) setTerminationDate('');
                  }}
                >
                  {Object.entries(employmentStatusLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Termination Date
                <input
                  type="date"
                  value={terminationDate}
                  aria-describedby="termination-date-hint"
                  onChange={(e) => {
                    setTerminationDate(e.target.value);
                    // Entering a date marks the employee Terminated (visibly, in the Status box above); nothing is saved until Save.
                    if (e.target.value && status !== 2) setStatus(2);
                  }}
                />
              </label>
              <label>
                SSN (last 4 digits)
                <input
                  value={ssnLast4}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="1234"
                  aria-describedby="ssn-last4-hint"
                  onChange={(e) => {
                    const next = applySsnLast4Input(e.target.value);
                    setSsnNotice(next.refusal);
                    if (next.value !== null) setSsnLast4(next.value);
                  }}
                />
              </label>
            </div>
            <p id="termination-date-hint" className="muted" style={{ marginTop: 8 }}>
              Entering a Termination Date sets Status to Terminated when you save.
            </p>
            <p id="ssn-last4-hint" className={ssnNotice ? 'error' : 'muted'} role={ssnNotice ? 'alert' : undefined} style={{ marginTop: 4 }}>
              {ssnNotice ?? 'SSN: only the last four digits are stored. A full Social Security number is not accepted.'}
            </p>
            {saveError && <p className="error">{saveError}</p>}
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={() => void handleSavePersonalInfo()} disabled={saving}>
                {saving ? 'Saving…' : 'Save Personal Information'}
              </button>
            </div>
            <p className="muted" style={{ marginTop: 10 }}>
              The other sections on the left are being connected one at a time and cannot be edited yet.
            </p>
          </section>
        )}

        {sub === 'address' && <EmployeeAddressSection employee={employee} />}

        {sub === 'work' && (
          <>
            <WorkDetailsSection employee={employee} onEmployeeUpdated={onEmployeeUpdated} />
            {/* Job Role Library: the current job role and its history, read-only. Editing stays in Work Details above. */}
            <EmployeeRoleSection employee={employee} />
          </>
        )}

        {sub === 'location' && <WorkLocationSection employee={employee} />}

        {sub === 'payment' && <EmployeePayMethodSection employee={employee} onSaved={onPayRatesChanged} />}

        {sub === 'payinfo' && <EmployeePayScheduleSection employee={employee} />}

        {sub === 'payroll' && (
          <PayrollDetailsSection employee={employee} onEmployeeUpdated={onEmployeeUpdated} onPayRatesChanged={onPayRatesChanged} />
        )}

        {footer}
      </div>
    </div>
  );
}
